import React, { useEffect, useState } from 'react';
import { buildApiUrl } from '../config/api';
import SearchBar from '../components/SearchBar';

export interface Item {
    id: string | number | null;
    name: string;
    price?: string | null;
    description?: string | null;
    imageUrl?: string | null;
    url?: string | null;
    source?: string;
    rating?: number | null;
}

interface ApiResponse {
    success: boolean;
    message: string;
    data: Item[];
}

type MarketplaceProps = {
    onProvideSearch?: (fn: (query: string) => void) => void;
    onSearchStart?: () => void;
    onSearchComplete?: (hasResults: boolean) => void;
    onResults?: (items: Item[], loading: boolean, error: string | null) => void;
    hideResults?: boolean;
};

const Marketplace: React.FC<MarketplaceProps> = ({
    onProvideSearch,
    onSearchStart,
    onSearchComplete,
    onResults,
    hideResults = false,
}) => {
    const [items, setItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchItems = async (query: string) => {
        const trimmed = query.trim();
        if (!trimmed) {
            setItems([]);
            setError(null);
            setLoading(false);
            if (onSearchComplete) onSearchComplete(false);
            if (onResults) onResults([], false, null);
            return;
        }

        setLoading(true);
        setError(null);
        if (onSearchStart) onSearchStart();
        if (onResults) onResults([], true, null);

        try {
            const response = await fetch(`${buildApiUrl('websearch.php')}?query=${encodeURIComponent(trimmed)}`);
            const data: ApiResponse = await response.json();

            if (data.success) {
                setItems(data.data);
                if (onSearchComplete) onSearchComplete((data.data || []).length > 0);
                if (onResults) onResults(data.data, false, null);
            } else {
                setError(data.message);
                setItems([]);
                if (onSearchComplete) onSearchComplete(false);
                if (onResults) onResults([], false, data.message);
            }
        } catch (err) {
            console.error('Error fetching items:', err);
            const message = 'Failed to fetch items. Please try again.';
            setError(message);
            setItems([]);
            if (onSearchComplete) onSearchComplete(false);
            if (onResults) onResults([], false, message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (onProvideSearch) {
            onProvideSearch(fetchItems);
        }
    }, [onProvideSearch]);

    if (hideResults) {
        return null;
    }

    return (
        <div>
            <div className="search-section">
                <h2 className="search-title">Find Your Favorite Digital Products</h2>
            <SearchBar onSearch={fetchItems} />
            </div>

            <div className="results-section">
                {error && (
                    <div className="error">
                        <p>{error}</p>
                    </div>
                )}

            {loading ? (
                    <div className="loading">
                        <p>Searching...</p>
                    </div>
                ) : items.length > 0 ? (
                    <>
                        <h2 className="results-title">Search Results ({items.length} items)</h2>
                <ul>
                    {items.map((item) => (
                                <li key={String(item.id) + String(item.url)}>
                                    <div style={{ marginBottom: '12px' }}>
                                        {item.imageUrl ? (
                                            <img
                                                src={item.imageUrl}
                                                alt={item.name}
                                                style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '10px' }}
                                            />
                                        ) : (
                                            <div className="img-placeholder" aria-label="No image" />
                                        )}
                                    </div>
                                    <h3>
                                        {item.url ? (
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#ff6b35', textDecoration: 'none' }}>
                                                {item.name}
                                            </a>
                                        ) : (
                                            item.name
                                        )}
                                    </h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '6px' }}>
                                        {item.price && <p style={{ margin: 0 }}><strong>Price:</strong> {item.price}</p>}
                                        {typeof item.rating === 'number' && (
                                            <div className="rating-stars" aria-label={`Rating ${item.rating}`}>
                                                {Array.from({ length: 5 }).map((_, i) => (
                                                    <span key={i} className={i < Math.round(item.rating || 0) ? 'star filled' : 'star'}>★</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </li>
                    ))}
                </ul>
                    </>
                ) : !loading && (
                    <div className="no-results">
                        <p>No items found. Try a different keyword!</p>
                    </div>
            )}
            </div>
        </div>
    );
};

export default Marketplace;